<script setup>
import { ref } from "vue";

const props = defineProps({
  peleaActual: { type: Object, default: null },
  esTiempoAgotado: { type: Boolean, default: false },
  esActivo: { type: Boolean, default: true },
});

const emit = defineEmits(["declarar", "cancelar"]);

const tipoResultado = ref("puntos");
const confirmarColor = ref(null); // 'rojo' | 'azul' | null — paso de confirmación

const tiposResultado = [
  { value: "puntos", label: "Por Puntos" },
  { value: "sumision", label: "Sumisión" },
  { value: "wo", label: "W.O." },
  { value: "descalif", label: "Descalif." },
];

function seleccionarGanador(color) {
  if (!props.esActivo) return;
  confirmarColor.value = color;
}

function confirmar() {
  emit("declarar", { color: confirmarColor.value, tipo: tipoResultado.value });
  confirmarColor.value = null;
}

function cancelarConfirmacion() {
  confirmarColor.value = null;
}
</script>

<template>
  <div class="declarar-panel" :class="{ urgente: esTiempoAgotado }">
    <!-- Header -->
    <div class="panel-header">
      <span class="status-badge" :class="{ agotado: esTiempoAgotado }">
        {{ esTiempoAgotado ? "⏱ TIEMPO AGOTADO" : "🎖 FINAL DE COMBATE" }}
      </span>
      <span class="instruccion">Selecciona método y ganador</span>
      <button v-if="!esTiempoAgotado" class="btn-cancelar" @click="$emit('cancelar')">
        ✕ Cancelar
      </button>
    </div>

    <!-- Métodos -->
    <div class="metodo-row">
      <button
        v-for="t in tiposResultado"
        :key="t.value"
        class="btn-metodo"
        :class="{ activo: tipoResultado === t.value }"
        @click="tipoResultado = t.value"
      >
        {{ t.label }}
      </button>
    </div>

    <!-- Paso 1: seleccionar ganador -->
    <div v-if="!confirmarColor" class="action-grid">
      <button
        class="btn-win rojo"
        :disabled="!esActivo"
        @click="seleccionarGanador('rojo')"
      >
        <span class="win-label">GANA ROJO</span>
        <span class="win-name">{{ peleaActual?.rojo?.nombre || "BYE" }}</span>
      </button>
      <button
        class="btn-win azul"
        :disabled="!esActivo"
        @click="seleccionarGanador('azul')"
      >
        <span class="win-label">GANA AZUL</span>
        <span class="win-name">{{ peleaActual?.azul?.nombre || "BYE" }}</span>
      </button>
    </div>

    <!-- Paso 2: confirmar inline (sin Swal) -->
    <div v-else class="confirm-panel" :class="confirmarColor">
      <div class="confirm-info">
        <span class="confirm-label">GANADOR</span>
        <span class="confirm-nombre">
          {{
            confirmarColor === "rojo"
              ? peleaActual?.rojo?.nombre || "BYE"
              : peleaActual?.azul?.nombre || "BYE"
          }}
        </span>
        <span class="confirm-metodo">
          {{ tiposResultado.find((t) => t.value === tipoResultado)?.label }}
        </span>
      </div>
      <div class="confirm-actions">
        <button class="btn-volver" @click="cancelarConfirmacion">← Volver</button>
        <button class="btn-confirmar" :class="confirmarColor" @click="confirmar">
          ✓ Confirmar
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;600;800&family=DM+Sans:wght@400;600&display=swap");

.declarar-panel {
  background: #0f172a;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.declarar-panel.urgente {
  border-top-color: rgba(239, 68, 68, 0.4);
  background: linear-gradient(to bottom, rgba(239, 68, 68, 0.06), #0f172a);
}

/* Header */
.panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.status-badge {
  font-family: "Bebas Neue", sans-serif;
  padding: 4px 12px;
  border-radius: 6px;
  background: #1e293b;
  color: #94a3b8;
  font-size: 14px;
  letter-spacing: 1px;
  flex-shrink: 0;
}
.status-badge.agotado {
  background: rgba(127, 29, 29, 0.6);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.instruccion {
  font-size: 11px;
  color: #475569;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  flex: 1;
}
.btn-cancelar {
  padding: 5px 14px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  font-family: "DM Sans", sans-serif;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-cancelar:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
}

/* Métodos */
.metodo-row {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  overflow-x: auto;
  padding-bottom: 3px;
}
.btn-metodo {
  flex-shrink: 0;
  padding: 7px 14px;
  border-radius: 20px;
  border: 1.5px solid #334155;
  background: #1e293b;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  font-family: "DM Sans", sans-serif;
}
.btn-metodo.activo {
  background: white;
  color: #0f172a;
  border-color: white;
}
.btn-metodo:hover:not(.activo) {
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.7);
}

/* Ganadores — paso 1 */
.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.btn-win {
  padding: 14px 12px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  transition: all 0.15s;
  font-family: "DM Sans", sans-serif;
}
.btn-win:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.btn-win:not(:disabled):hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
}
.btn-win:active {
  transform: scale(0.97);
}
.btn-win.rojo {
  background: linear-gradient(135deg, #991b1b 0%, #ef4444 100%);
}
.btn-win.azul {
  background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%);
}
.win-label {
  font-family: "Bebas Neue", sans-serif;
  font-size: 13px;
  letter-spacing: 2px;
  opacity: 0.75;
  color: white;
}
.win-name {
  font-size: 14px;
  font-weight: 800;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: center;
}

/* Confirmación inline — paso 2 */
.confirm-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  flex-wrap: wrap;
}
.confirm-panel.rojo {
  background: linear-gradient(135deg, rgba(153, 27, 27, 0.6), rgba(239, 68, 68, 0.3));
  border: 1px solid rgba(239, 68, 68, 0.4);
}
.confirm-panel.azul {
  background: linear-gradient(135deg, rgba(29, 78, 216, 0.6), rgba(59, 130, 246, 0.3));
  border: 1px solid rgba(59, 130, 246, 0.4);
}

.confirm-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.confirm-label {
  font-family: "Bebas Neue", sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  opacity: 0.65;
  color: white;
}
.confirm-nombre {
  font-size: 18px;
  font-weight: 800;
  color: white;
  text-transform: uppercase;
}
.confirm-metodo {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.confirm-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}
.btn-volver {
  padding: 9px 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  transition: all 0.15s;
}
.btn-volver:hover {
  background: rgba(255, 255, 255, 0.14);
  color: white;
}

.btn-confirmar {
  padding: 9px 22px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  transition: all 0.15s;
  color: white;
}
.btn-confirmar.rojo {
  background: #b91c1c;
}
.btn-confirmar.azul {
  background: #1d4ed8;
}
.btn-confirmar:hover {
  filter: brightness(1.15);
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
  .declarar-panel {
    padding: 12px 14px;
  }
  .action-grid {
    gap: 8px;
  }
  .btn-win {
    padding: 12px 8px;
  }
  .win-name {
    font-size: 12px;
  }
  .confirm-panel {
    flex-direction: column;
    align-items: flex-start;
  }
}
@media (max-width: 480px) {
  .metodo-row {
    gap: 4px;
  }
  .btn-metodo {
    padding: 5px 10px;
    font-size: 10px;
  }
  .win-name {
    font-size: 11px;
  }
  .confirm-nombre {
    font-size: 15px;
  }
}
</style>
